import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const MyOrders = () => {
  document.title = "My Orders"

  const { orders } = useSelector((state) => state.order);
  const { userInfo } = useSelector((state) => state.auth);

  const getPaymentMethodBadge = (method) => {
    const badges = {
      cod: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Cash on Delivery' },
      paypal: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'PayPal' },
      upi: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'UPI' }
    };
    const badge = badges[method?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-800', label: method || 'N/A' };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900">
          My Orders
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Track and manage your order history
        </p>
      </div>

      <div className="space-y-6">
        {userInfo && orders && orders.length > 0 ? (
          <>
            {orders.map((order, index) => (
              <div key={order._id || index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                {/* Order Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Order ID</p>
                        <p className="text-sm font-mono font-medium text-gray-900 mt-1">
                          {order.paymentID ? order.paymentID.substring(0, 20) : 'N/A'}
                        </p>
                      </div>
                      <div className="hidden sm:block w-px h-10 bg-gray-300"></div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getPaymentMethodBadge(order.paymentMethod)}
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {order.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Products Section */}
                    <div className="lg:col-span-2">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Order Items</h4>
                      <div className="space-y-4">
                        {order.products && order.products.length > 0 ? (
                          order.products.map((item, idx) => {
                            const productItem = item[0] || item;
                            const product = productItem.product || {};
                            return (
                              <Link
                                key={idx}
                                to={`/products/${product._id || ''}`}
                                className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              >
                                {product.img && product.img[0] && (
                                  <div className="flex-shrink-0 w-20 h-20 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <img
                                      className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                                      src={product.img[0]}
                                      alt={product.title || 'Product'}
                                    />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-emerald-600 transition-colors">
                                    {product.title || 'Product'}
                                  </p>
                                  <p className="text-sm text-gray-500 mt-1">
                                    Quantity: {productItem.quantity || 1}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-gray-900">
                                    ₹{(productItem.itemTotal || 0).toFixed(2)}
                                  </p>
                                </div>
                              </Link>
                            );
                          })
                        ) : (
                          <p className="text-sm text-gray-500">No items</p>
                        )}
                      </div>
                    </div>

                    {/* Order Summary & Delivery */}
                    <div className="lg:col-span-1 space-y-6">
                      {/* Payment Summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Summary</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Total Amount</span>
                            <span className="font-semibold text-gray-900">
                              ₹{order.amount ? order.amount.toFixed(2) : '0.00'}
                            </span>
                          </div>
                          {order.paymentMethod === 'cod' && order.advanceAmount && (
                            <>
                              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                                <span className="text-gray-600">Advance Paid</span>
                                <span className="font-medium text-green-600">
                                  ₹{order.advanceAmount.toFixed(2)}
                                </span>
                              </div>
                              {order.remainingAmount > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-amber-700 font-medium">Pay on Delivery</span>
                                  <span className="font-bold text-amber-700">
                                    ₹{order.remainingAmount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Delivery Address */}
                      {order.address && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Delivery Address</h4>
                          <address className="not-italic text-sm text-gray-600 space-y-1">
                            <p className="font-medium text-gray-900">
                              {order.address.firstname} {order.address.lastname}
                            </p>
                            <p>{order.address.streetAddress || order.address.address}</p>
                            <p>{order.address.city}, {order.address.state}</p>
                            {order.address.zipcode && <p>{order.address.zipcode}</p>}
                            <p className="pt-2 font-medium text-gray-900">
                              {order.address.phone}
                            </p>
                          </address>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No orders yet</h3>
            <p className="mt-2 text-sm text-gray-500">Start shopping to see your orders here</p>
            <Link 
              to="/products" 
              className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
